import { apiDelete, apiGet, apiPatch, apiPost } from 'src/lib/api/axios';
import { ResourcePermission, ResourcePermissionRole, TenantShare } from 'src/features/chat/types';
import { DocumentHubNode, FolderContentsResponse } from './types';

export type AnalysisStatus = 'pending' | 'extracting' | 'tagging' | 'completed' | 'failed';

/** Statuses of an analysis run still under way. */
export const ANALYSIS_IN_PROGRESS_STATUSES: ReadonlySet<string> = new Set<AnalysisStatus>([
  'pending',
  'extracting',
  'tagging'
]);

export interface AnalysisSummary {
  analysis_status: AnalysisStatus | null;
  error_message: string | null;
  tags: {
    group: 'document_type' | 'domain' | 'site' | 'national_returns';
    slug: string;
    label: string;
    confirmed: boolean;
  }[];
  has_suggestions: boolean;
  expiry_status: 'expired' | 'expiring_soon' | null;
}

/** A document's latest analysis run and the classification the hub shows for it. */
export interface AnalysisSummaryResponse {
  /** Null when the document has never been analysed. */
  analysis_status: AnalysisStatus | null;
  error_message: string | null;
  /** A document type slug, unreviewed suggestions included. */
  document_type: string | null;
  document_type_confirmed: boolean;
  domains: string[];
  domains_confirmed: boolean;
  sites: string[];
  has_suggestions: boolean;
  expiry_status: 'expired' | 'expiring_soon' | null;
}

export async function getDocumentAnalysisSummary(documentId: string): Promise<AnalysisSummaryResponse> {
  const response = await apiGet<AnalysisSummaryResponse>(`files/analysis/${documentId}/summary`, {
    meta: { globalError: false, context: 'get-document-analysis-summary' }
  });
  return response.data;
}

export interface FilterOptions {
  resource_tags: DocumentHubNode['tags'];
  source: string[];
}

export async function getFilterOptions(folderId?: string | null): Promise<FilterOptions> {
  const params = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '';
  const response = await apiGet<FilterOptions>(`document-hub/filter-options${params}`, {
    meta: { context: 'get-document-hub-filter-options' }
  });
  return response.data;
}

/** Folder hierarchy only — no files. Stays small regardless of document volume. */
export async function getDocumentTree(): Promise<DocumentHubNode[]> {
  const response = await apiGet<DocumentHubNode[]>('document-hub/tree', {
    meta: { context: 'get-document-hub-tree' }
  });
  return response.data;
}

export interface FolderContentsParams {
  search?: string;
  offset?: number;
  limit?: number;
  tag?: string[];
  source?: string[];
  files_only?: boolean;
  hub_document_type?: string[];
  folder_type?: string[];
  exclude_document_id?: string[];
  omit_drafts_and_workflows?: boolean;
  sort_by?: 'name' | 'updated_at';
  sort_desc?: boolean;
}

function buildFolderParams({
  search,
  offset,
  limit,
  tag,
  source,
  files_only,
  hub_document_type,
  folder_type,
  exclude_document_id,
  omit_drafts_and_workflows,
  sort_by,
  sort_desc
}: FolderContentsParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (search) qs.append('search', search);
  if (offset !== undefined) qs.append('offset', String(offset));
  if (limit !== undefined) qs.append('limit', String(limit));
  tag?.forEach((v) => qs.append('tag', v));
  source?.forEach((v) => qs.append('source', v));
  if (files_only) qs.append('files_only', 'true');
  hub_document_type?.forEach((v) => qs.append('hub_document_type', v));
  folder_type?.forEach((v) => qs.append('folder_type', v));
  exclude_document_id?.forEach((v) => qs.append('exclude_document_id', v));
  if (omit_drafts_and_workflows) qs.append('omit_drafts_and_workflows', 'true');
  if (sort_by) qs.append('sort_by', sort_by);
  if (sort_desc !== undefined) qs.append('sort_desc', sort_desc ? 'true' : 'false');
  return qs;
}

/**
 * Paginated direct children (subfolders + files) for a single folder.
 * Pass `folderId = null` for the hub root.
 */
export async function getFolderContents(
  folderId: string | null,
  params: FolderContentsParams = {}
): Promise<FolderContentsResponse> {
  const id = folderId ?? 'root';
  const qs = buildFolderParams(params).toString();
  const response = await apiGet<FolderContentsResponse>(`document-hub/folders/${id}/contents${qs ? `?${qs}` : ''}`, {
    meta: { context: 'get-document-hub-folder-contents' }
  });
  return response.data;
}

/**
 * All files across the hub with pagination (flat 'all files' view).
 */
export async function getAllFiles(params: FolderContentsParams = {}): Promise<FolderContentsResponse> {
  const qs = buildFolderParams(params).toString();
  const response = await apiGet<FolderContentsResponse>(`document-hub/files${qs ? `?${qs}` : ''}`, {
    meta: { context: 'get-document-hub-all-files' }
  });
  return response.data;
}

export async function getDocumentHubLocation(documentId: string): Promise<{ folders: { id: string; name: string }[] }> {
  const response = await apiGet<{ folders: { id: string; name: string }[] }>(
    `document-hub/documents/${documentId}/location`,
    { meta: { context: 'get-document-hub-location' } }
  );
  return response.data;
}

export async function renameDocumentFilename(documentId: string, filename: string): Promise<void> {
  await apiPatch(
    `document-hub/documents/${documentId}/filename`,
    { filename },
    {
      meta: { context: 'rename-document-filename' }
    }
  );
}

export async function renameUserFolder(folderId: string, name: string): Promise<void> {
  const id = folderId.startsWith('uf/') ? folderId.slice(3) : folderId;
  await apiPatch(`document-hub/folders/${id}`, { name }, { meta: { context: 'rename-document-folder' } });
}

export async function moveUserFolder(folderId: string, parentId: string): Promise<void> {
  const id = folderId.startsWith('uf/') ? folderId.slice(3) : folderId;
  await apiPatch(
    `document-hub/folders/${id}/parent`,
    { parent_id: parentId },
    { meta: { context: 'move-document-folder' } }
  );
}

export async function countFilesInFolder(folderId: string): Promise<number> {
  const id = folderId.startsWith('uf/') ? folderId.slice(3) : folderId;
  const response = await apiGet<{ count: number }>(`document-hub/folders/${id}/file-count`, {
    meta: { context: 'count-document-folder-files' }
  });
  return response.data.count;
}

export async function deleteUserFolder(folderId: string): Promise<void> {
  const id = folderId.startsWith('uf/') ? folderId.slice(3) : folderId;
  await apiDelete(`document-hub/folders/${id}`, { meta: { context: 'delete-document-folder' } });
}

export async function createUserFolder(name: string, parentId: string): Promise<DocumentHubNode> {
  const response = await apiPost<DocumentHubNode>(
    'document-hub/folders',
    { name, parent_id: parentId },
    { meta: { context: 'create-document-folder' } }
  );
  return response.data;
}

export async function fileDocumentsInFolder(parentId: string, documentIds: string[]): Promise<void> {
  await apiPost(
    'document-hub/folders/file',
    { parent_id: parentId, document_ids: documentIds },
    { meta: { context: 'file-documents-in-folder' } }
  );
}

export async function relocateUserFolder(
  folderId: string,
  destination: 'personal' | 'organisation' | 'group',
  groupId?: string
): Promise<void> {
  const id = folderId.startsWith('uf/') ? folderId.slice(3) : folderId;
  await apiPatch(
    `document-hub/folders/${id}/location`,
    { destination, group_id: groupId ?? null },
    { meta: { context: 'relocate-document-folder' } }
  );
}

export async function relocateDocument(
  documentId: string,
  destination: 'personal' | 'organisation' | 'group',
  groupId?: string
): Promise<void> {
  await apiPatch(
    `document-hub/documents/${documentId}/move`,
    { destination, group_id: groupId ?? null },
    {
      meta: { context: 'relocate-document' }
    }
  );
}

export async function shareWithOrganisation(documentId: string): Promise<void> {
  await apiPatch(
    `document-hub/documents/${documentId}/share-with-organisation`,
    {},
    {
      meta: { context: 'share-document-with-organisation' }
    }
  );
}

export async function unshareFromOrganisation(documentId: string): Promise<void> {
  await apiPatch(
    `document-hub/documents/${documentId}/unshare-from-organisation`,
    {},
    {
      meta: { context: 'unshare-document-from-organisation' }
    }
  );
}

export async function unshareFromGroup(documentId: string, groupId: string): Promise<void> {
  await apiPatch(
    `document-hub/documents/${documentId}/unshare-from-group`,
    { group_id: groupId },
    {
      meta: { context: 'unshare-document-from-group' }
    }
  );
}

export async function linkOrganisationLibraryDocuments(groupId: string, documentIds: string[]): Promise<void> {
  await apiPost(
    `document-hub/groups/${groupId}/linked-documents`,
    { document_ids: documentIds },
    {
      meta: { context: 'link-organisation-library-documents' }
    }
  );
}

export async function moveDocumentsToGroup(groupId: string, documentIds: string[]): Promise<void> {
  await apiPost(
    `document-hub/groups/${groupId}/moved-documents`,
    { document_ids: documentIds },
    {
      meta: { context: 'move-documents-to-group' }
    }
  );
}

export async function deleteNode(nodeId: string): Promise<void> {
  await apiDelete(`document-hub/nodes/${nodeId}`, {
    meta: { context: 'delete-document-hub-node' }
  });
}

export interface DocumentAlertItem {
  document_id: string;
  name: string;
  expiry_date: string | null;
  expiry_status: 'expired' | 'expiring_soon' | 'analysis_failed';
  subtitle: string | null;
}

export async function getHubAlerts(): Promise<DocumentAlertItem[]> {
  const response = await apiGet<DocumentAlertItem[]>('document-hub/alerts', {
    meta: { globalError: false, context: 'get-document-hub-alerts' }
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// Per-document permission management
// ---------------------------------------------------------------------------

export interface DocumentSharesResponse {
  user_shares: ResourcePermission[];
  group_shares: { group_id: string; group_name: string; permission: ResourcePermissionRole }[];
  tenant_share?: TenantShare | null;
}

export async function getDocumentShares(documentId: string): Promise<DocumentSharesResponse> {
  const response = await apiGet<DocumentSharesResponse>(`document-hub/documents/${documentId}/shares`, {
    meta: { globalError: false, context: 'get-document-shares' }
  });
  return response.data;
}

export async function addDocumentShares(
  documentId: string,
  emails: string[],
  permission: ResourcePermissionRole,
  message?: string,
  groupIds?: string[]
): Promise<ResourcePermission[]> {
  const response = await apiPost<ResourcePermission[]>(
    `document-hub/documents/${documentId}/shares`,
    { emails: emails.length > 0 ? emails : undefined, permission, message, group_ids: groupIds },
    { meta: { globalError: false, context: 'add-document-shares' } }
  );
  return response.data;
}

export async function updateDocumentShare(
  documentId: string,
  permissionId: string,
  role: ResourcePermissionRole
): Promise<void> {
  await apiPatch(`document-hub/documents/${documentId}/shares/${permissionId}?role=${role}`, {
    meta: { globalError: false, context: 'update-document-share' }
  });
}

export async function deleteDocumentShare(documentId: string, permissionId: string): Promise<void> {
  await apiDelete(`document-hub/documents/${documentId}/shares/${permissionId}`, {
    meta: { globalError: false, context: 'delete-document-share' }
  });
}

export async function deleteDocumentGroupShare(documentId: string, groupId: string): Promise<void> {
  await apiDelete(`document-hub/documents/${documentId}/group-shares/${groupId}`, {
    meta: { globalError: false, context: 'delete-document-group-share' }
  });
}
